import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { Outlet, useLocation } from "react-router";
import { useAuth } from "../shared/auth/AuthContext";
import { normalizeLoginHint } from "../shared/auth/AuthService";
import { PlatformGate } from "../features/platform/PlatformGate";
import { TenantGate } from "./TenantGate";
import { useI18n } from "../shared/i18n/I18nContext";
import { cx } from "../shared/ui/classNames";
import { Field } from "../shared/ui/Field";
import { useFocusFirstInvalid } from "../shared/ui/focus";
import { Loading } from "../shared/ui/Loading";
import { PublicLayout } from "../shared/ui/PublicLayout";
import styles from "../shared/ui/ui.module.css";

/**
 * Route guard: renders child routes for a stored session (also offline with an expired token) and
 * starts the login otherwise. The native apps log in through the system browser; if the person
 * closes it, the button starts the login again. Where the service asks for the work e-mail address
 * first (the native apps, T-117), the page shows the address field instead of redirecting at once.
 *
 * @returns The child routes or a redirect notice.
 */
export function RequireAuth(): JSX.Element {
  const { status, service } = useAuth();
  const { t, locale } = useI18n();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}`;

  useEffect(() => {
    if (status === "signedOut" && !service.emailFirst) {
      void service.login(returnTo, locale);
    }
  }, [status, service, returnTo, locale]);

  if (status === "loading") {
    const loading = <Loading />;
    return loading;
  }

  if (status === "signedOut" && service.emailFirst) {
    const emailFirst = <EmailFirstSignIn returnTo={returnTo} />;
    return emailFirst;
  }

  if (status === "signedOut") {
    const redirecting = (
      <PublicLayout title={t("auth.signIn")} look="hero">
        <p role="status">{t("auth.redirecting")}</p>
        <div>
          <button
            type="button"
            className={cx(styles["button"], styles["primary"])}
            onClick={() => {
              void service.login(returnTo, locale);
            }}
          >
            {t("auth.signIn")}
          </button>
        </div>
      </PublicLayout>
    );
    return redirecting;
  }

  // Operator staff go to the operator area and never to the tenant selection or the clock (F-69).
  const outlet = (
    <PlatformGate>
      <TenantGate>
        <Outlet />
      </TenantGate>
    </PlatformGate>
  );
  return outlet;
}

/** Properties of {@link EmailFirstSignIn}. */
interface EmailFirstSignInProps {
  /** Application path to open after the login. */
  readonly returnTo: string;
}

/**
 * Sign-in page of the native apps: asks for the work e-mail address, from which the identity
 * provider finds the customer's organization and single sign-on (MT-15). The address is not stored.
 * The hint and an error belong to the field (UX-111); after a failed attempt the field takes the
 * focus and is checked while typing (UX-110, UX-120).
 *
 * @param props - The properties.
 * @returns The page.
 */
function EmailFirstSignIn(props: EmailFirstSignInProps): JSX.Element {
  const { returnTo } = props;
  const { service } = useAuth();
  const { t, locale } = useI18n();
  const [email, setEmail] = useState("");
  const [failedSubmits, setFailedSubmits] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const invalid = failedSubmits > 0 && normalizeLoginHint(email) === null;

  useFocusFirstInvalid(formRef, failedSubmits);

  const page = (
    <PublicLayout title={t("auth.emailFirst.title")} look="signIn">
      <form
        ref={formRef}
        noValidate
        className={styles["stack"]}
        onSubmit={(event) => {
          event.preventDefault();
          const hint = normalizeLoginHint(email);

          if (hint === null) {
            setFailedSubmits((count) => count + 1);
            return;
          }

          void service.login(returnTo, locale, hint);
        }}
      >
        <Field
          label={t("auth.emailFirst.label")}
          hint={t("auth.emailFirst.hint")}
          error={invalid ? t("auth.emailFirst.invalid") : null}
        >
          {(control) => (
            <input
              {...control}
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          )}
        </Field>
        <div>
          <button type="submit" className={cx(styles["button"], styles["primary"])}>
            {t("auth.emailFirst.continue")}
          </button>
        </div>
      </form>
    </PublicLayout>
  );
  return page;
}
